import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MediaInteractionModal from '../components/MediaInteractionModal';
import api from '../utils/api';
import './UserSearch.css';

const ROLE_OPTIONS = [
  'Actor', 'Audience', 'Director', 'Writer', 'Cameraman', 'Editor', 'Producer',
  'Cinematographer', 'Sound Designer', 'Music Composer', 'Costume Designer',
  'Makeup Artist', 'Production Designer', 'Art Director', 'VFX Artist',
  'Stunt Coordinator', 'Casting Director', 'Line Producer', 'Assistant Director',
  'Gaffer', 'Grip'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

interface User {
  id: number;
  name: string;
  email: string;
  city?: string;
  state?: string;
  roles?: string[];
  profile_picture?: string;
  profile_completed: boolean;
  connectionStatus: string | null;
  connectionInitiatedBy: number | null;
  isFollowing: boolean;
}

interface MediaItem {
  id: number;
  file_name: string;
  media_type: string;
  is_collab?: boolean;
  collaborator_role?: string | null;
  owner_name?: string | null;
}

interface Collaborator {
  id: number;
  media_id: number;
  role_name: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  user_roles: string[];
  user_city: string | null;
  user_state: string | null;
}

interface UserProfile {
  user: {
    id: number;
    name: string;
    email: string;
    city?: string;
    state?: string;
    roles?: string[];
    profile_picture?: string;
    profile_completed?: boolean;
  };
  connectionStatus: string | null;
  connectionInitiatedBy: number | null;
  isFollowing: boolean;
  connectionCount?: number;
  followerCount?: number;
  photos: MediaItem[];
  videos: MediaItem[];
}

interface DirectMessageLocationState {
  preselectedConversation?: {
    user_id: number;
    name: string;
    email: string;
    profile_picture?: string | null;
    last_message: string;
    last_message_time: string;
    unread_count: number;
  };
}

const sortUsersByProfileCompletion = (list: User[]) =>
  [...list].sort((a, b) => {
    const aCompleted = a.profile_completed !== false;
    const bCompleted = b.profile_completed !== false;

    if (aCompleted !== bCompleted) {
      return aCompleted ? -1 : 1;
    }

    // Keep the randomized API order within each completion group.
    return 0;
  });

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

const UserSearchPage: React.FC = () => {
  const navigate = useNavigate();

  const [users,          setUsers]          = useState<User[]>([]);
  const [filteredUsers,  setFilteredUsers]  = useState<User[]>([]);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [selectedRoles,  setSelectedRoles]  = useState<string[]>([]);
  const [showFilters,    setShowFilters]    = useState(false);
  const [friendsOnly,    setFriendsOnly]    = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [message,        setMessage]        = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const availableRoles = Array.from(
    new Set([
      ...ROLE_OPTIONS,
      ...users.flatMap(user => user.roles || []),
    ])
  ).sort((a, b) => a.localeCompare(b));

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line

  useEffect(() => { applyFilters(); }, [searchQuery, selectedRoles, friendsOnly, users]); // eslint-disable-line

  const loadUsers = async () => {
    try {
      const rolesParam = selectedRoles.length > 0 ? `&roles=${selectedRoles.join(',')}` : '';
      const response = await api.get(`/user/search?query=${searchQuery}${rolesParam}`);
      const sortedUsers = sortUsersByProfileCompletion(response.data.users || []);
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    if (friendsOnly) {
      filtered = filtered.filter(u => u.connectionStatus === 'accepted');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.roles || []).some(role => role.toLowerCase().includes(q))
      );
    }

    if (selectedRoles.length > 0) {
      filtered = filtered.filter(u =>
        (u.roles || []).some(role =>
          selectedRoles.some(selectedRole => selectedRole.toLowerCase() === role.toLowerCase())
        )
      );
    }

    setFilteredUsers(sortUsersByProfileCompletion(filtered));
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleClearFilters = () => {
    setSelectedRoles([]);
    setSearchQuery('');
    setFriendsOnly(false);
  };

  const handleConnect = async (user: User) => {
    if (!user.profile_completed) {
      setError('Receiver profile yet to be completed');
      setTimeout(() => setError(''), 2500);
      return;
    }
    try {
      await api.post('/connections/request', { targetUserId: user.id });
      setMessage('Connection request sent!');
      setTimeout(() => setMessage(''), 2000);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send connection request');
    }
  };

  const handleStartChat = (targetUserId: number) => {
    const targetUser = users.find((user) => user.id === targetUserId);
    const state: DirectMessageLocationState | undefined = targetUser
      ? {
          preselectedConversation: {
            user_id: targetUser.id,
            name: targetUser.name,
            email: targetUser.email,
            profile_picture: targetUser.profile_picture ?? null,
            last_message: '',
            last_message_time: new Date().toISOString(),
            unread_count: 0,
          },
        }
      : undefined;

    navigate(`/direct-messages/${targetUserId}`, state ? { state } : undefined);
  };

  const updateFollowState = (userId: number, isFollowing: boolean) => {
    setUsers((current) =>
      sortUsersByProfileCompletion(
        current.map((entry) => (entry.id === userId ? { ...entry, isFollowing } : entry)),
      ),
    );
  };

  const handleFollowToggle = async (userId: number, isFollowing: boolean) => {
    try {
      let nextIsFollowing = !isFollowing;
      if (isFollowing) {
        const response = await api.delete(`/users/${userId}/follow`);
        nextIsFollowing = Boolean(response.data?.isFollowing);
        setMessage('Unfollowed successfully!');
      } else {
        const response = await api.post(`/users/${userId}/follow`);
        nextIsFollowing = Boolean(response.data?.isFollowing);
        setMessage('Following successfully!');
      }
      updateFollowState(userId, nextIsFollowing);
      setTimeout(() => setMessage(''), 2000);
      return nextIsFollowing;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update follow');
      setTimeout(() => setError(''), 2500);
      throw err;
    }
  };

  const handleDisconnect = async (userId: number) => {
    try {
      await api.delete(`/connections/${userId}`);
      setUsers((current) =>
        sortUsersByProfileCompletion(
          current.map((entry) =>
            entry.id === userId
              ? { ...entry, connectionStatus: null, connectionInitiatedBy: null }
              : entry,
          ),
        ),
      );
      setMessage('Disconnected successfully!');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disconnect');
      setTimeout(() => setError(''), 2500);
      throw err;
    }
  };

  const getConnectionButton = (user: User) => {
    if (user.connectionStatus === 'accepted') {
      return (
        <>
          <button onClick={() => handleStartChat(user.id)} className="us-btn us-btn--ghost">
          💬 Chat
          </button>
          <button onClick={() => handleDisconnect(user.id).catch(() => {})} className="us-btn us-btn--ghost">
            Disconnect
          </button>
        </>
      );
    } else if (user.connectionStatus === 'pending') {
      return <span className="us-status-badge">Pending</span>;
    } else {
      return (
        <button onClick={() => handleConnect(user)} className="us-btn us-btn--filled">
          Connect
        </button>
      );
    }
  };

  const friendCount = users.filter(u => u.connectionStatus === 'accepted').length;
  const activeFilterCount = selectedRoles.length + (friendsOnly ? 1 : 0);

  if (loading) {
    return <div className="us-loading">Loading…</div>;
  }

  return (
    <div className="us-container">

      {/* ── Header ── */}
      <div className="us-header">
        <button onClick={() => navigate('/content')} className="us-back-btn">
          ← Back
        </button>
        <h1>Find People</h1>
      </div>

      {/* ── Flash messages ── */}
      {error   && <div className="us-flash us-flash--error">{error}</div>}
      {message && <div className="us-flash us-flash--success">{message}</div>}

      {/* ── Search & filter ── */}
      <div className="us-search-section">
        <div className="us-search-row">
          <input
            type="text"
            placeholder="Search by name or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="us-search-input"
          />
          <button
            onClick={() => setFriendsOnly(prev => !prev)}
            className={`us-friends-btn ${friendsOnly ? 'active' : ''}`}
            title={`${friendCount} connected`}
          >
            {friendsOnly ? '★' : '☆'} Friends
            {friendCount > 0 && <span className="us-friends-count">{friendCount}</span>}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`us-filter-btn ${selectedRoles.length > 0 ? 'active' : ''}`}
          >
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
        </div>

        {showFilters && (
          <div className="us-filters-panel">
            <div className="us-filters-head">
              <span className="us-filters-label">Filter by Role</span>
              {activeFilterCount > 0 && (
                <button onClick={handleClearFilters} className="us-clear-btn">
                  Clear all
                </button>
              )}
            </div>
            <div className="us-roles-grid">
              {availableRoles.map((role) => (
                <button
                  key={role}
                  className={`us-role-chip ${selectedRoles.includes(role) ? 'selected' : ''}`}
                  onClick={() => handleRoleToggle(role)}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="us-results-count">
          {friendsOnly
            ? `Showing ${filteredUsers.length} friend${filteredUsers.length !== 1 ? 's' : ''}`
            : `Showing ${filteredUsers.length} of ${users.length} users`
          }
        </div>
      </div>

      {/* ── User list ── */}
      <div className="us-list">
        {filteredUsers.length === 0 ? (
          <div className="us-empty">
            {friendsOnly ? (
              <>
                <p>No connections yet.</p>
                <button onClick={() => setFriendsOnly(false)} className="us-btn us-btn--ghost">
                  Browse all users
                </button>
              </>
            ) : (
              <>
                <p>No users found matching your criteria.</p>
                {(searchQuery || selectedRoles.length > 0) && (
                  <button onClick={handleClearFilters} className="us-btn us-btn--ghost">
                    Clear Filters
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          filteredUsers.map((user) => {
            const dpUrl = getMediaUrl(user.profile_picture);
            return (
              <div key={user.id} className="us-card">
                {!user.profile_completed && (
                  <span className="us-profile-pending-badge">Yet to complete profile</span>
                )}

                {/* Avatar */}
                <div className="us-avatar">
                  {dpUrl
                    ? <img src={dpUrl} alt={user.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    : user.name.charAt(0).toUpperCase()
                  }
                </div>

                {/* Info */}
                <div className="us-info">
                  <h3>
                    {user.name}
                    {user.profile_completed && user.connectionStatus === 'accepted' && (
                      <span className="us-friend-tag">Connected</span>
                    )}
                  </h3>
                  <p className="us-email">{user.email}</p>
                  {user.roles && user.roles.length > 0 && (
                    <div className="us-roles">
                      {user.roles.slice(0, 3).map((role, i) => (
                        <span key={i} className="us-role-tag">{role}</span>
                      ))}
                      {user.roles.length > 3 && (
                        <span className="us-role-tag us-role-tag--more">+{user.roles.length - 3}</span>
                      )}
                    </div>
                  )}
                  {user.city && (
                    <p className="us-location">📍 {user.city}, {user.state}</p>
                  )}
                </div>

                {/* Actions */}
                {user.profile_completed && (
                  <div className="us-actions">
                    <button onClick={() => setSelectedUserId(user.id)} className="us-btn us-btn--ghost">
                      View
                    </button>
                    {getConnectionButton(user)}
                    <button
                      onClick={() => handleFollowToggle(user.id, Boolean(user.isFollowing)).catch(() => {})}
                      className={`us-btn us-btn--follow${user.isFollowing ? ' is-following' : ''}`}
                    >
                      {user.isFollowing ? 'Unfollow' : 'Follow'}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Profile modal ── */}
      {selectedUserId && (
        <UserProfileModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onConnect={(userId) => {
            const targetUser = users.find(user => user.id === userId);
            if (targetUser) handleConnect(targetUser);
          }}
          onDisconnect={async (userId) => {
            try {
              await api.delete(`/connections/${userId}`);
              setUsers((current) =>
                sortUsersByProfileCompletion(
                  current.map((entry) =>
                    entry.id === userId
                      ? { ...entry, connectionStatus: null, connectionInitiatedBy: null }
                      : entry,
                  ),
                ),
              );
              setMessage('Disconnected successfully!');
              setTimeout(() => setMessage(''), 2000);
            } catch (err: any) {
              setError(err.response?.data?.message || 'Failed to disconnect');
              setTimeout(() => setError(''), 2500);
              throw err;
            }
          }}
          onStartChat={handleStartChat}
          onFollowToggle={handleFollowToggle}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Profile Modal
// ─────────────────────────────────────────────────────────────

interface UserProfileModalProps {
  userId: number;
  onClose: () => void;
  onConnect: (userId: number) => void | Promise<void>;
  onDisconnect: (userId: number) => void | Promise<void>;
  onStartChat: (userId: number) => void;
  onFollowToggle: (userId: number, isFollowing: boolean) => boolean | Promise<boolean>;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  userId, onClose, onConnect, onDisconnect, onStartChat, onFollowToggle,
}) => {
  const [profile,        setProfile]        = useState<UserProfile | null>(null);
  const [viewMediaType,  setViewMediaType]  = useState<'photos' | 'videos'>('photos');
  const [loading,        setLoading]        = useState(true);
  const [dpError,        setDpError]        = useState(false);
  const [disconnecting,  setDisconnecting]  = useState(false);
  const [followUpdating, setFollowUpdating] = useState(false);

  const [lightbox,      setLightbox]      = useState<{
    url: string;
    isVideo: boolean;
    mediaId: number;
  } | null>(null);

  const [collabMap,     setCollabMap]     = useState<Record<number, Collaborator[]>>({});
  const [collabLoading, setCollabLoading] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setLoading(true);
    setDpError(false);
    setCollabMap({});
    api.get(`/user/${userId}/profile`)
      .then(res => setProfile(res.data))
      .catch(err => console.error('Failed to load profile:', err))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, onClose]);

  const fetchCollabs = useCallback(async (mediaId: number) => {
    if (collabMap[mediaId] !== undefined) return;
    setCollabLoading(prev => ({ ...prev, [mediaId]: true }));
    try {
      const res = await api.get(`/media/${mediaId}/collaborators`);
      setCollabMap(prev => ({ ...prev, [mediaId]: res.data.collaborators }));
    } catch {
      setCollabMap(prev => ({ ...prev, [mediaId]: [] }));
    } finally {
      setCollabLoading(prev => ({ ...prev, [mediaId]: false }));
    }
  }, [collabMap]);

  const openLightbox = (url: string, isVideo: boolean, mediaId: number) => {
    setLightbox({ url, isVideo, mediaId });
    fetchCollabs(mediaId);
  };

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await onDisconnect(userId);
      setProfile((current) =>
        current
          ? {
              ...current,
              connectionStatus: null,
              connectionInitiatedBy: null,
            }
          : current,
      );
    } finally {
      setDisconnecting(false);
    }
  }, [onDisconnect, userId]);

  const handleFollowToggle = useCallback(async () => {
    if (!profile) return;
    setFollowUpdating(true);
    try {
      const nextIsFollowing = await onFollowToggle(userId, Boolean(profile.isFollowing));
      setProfile((current) =>
        current
          ? {
              ...current,
              isFollowing: nextIsFollowing,
            }
          : current,
      );
    } finally {
      setFollowUpdating(false);
    }
  }, [onFollowToggle, profile, userId]);

  if (loading || !profile) {
    return (
      <div className="us-overlay" onClick={onClose}>
        <div className="us-modal" onClick={(e) => e.stopPropagation()}>
          <div className="us-loading" style={{ minHeight: 'unset', padding: '48px 0' }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  const dpUrl      = getMediaUrl(profile.user.profile_picture);
  const photos     = profile.photos || [];
  const videos     = profile.videos || [];
  const activeMedia = viewMediaType === 'photos' ? photos : videos;

  return (
    <>
      <div className="us-overlay" onClick={onClose}>
        <div className="us-modal" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="us-modal-close">✕</button>

          {/* ── Header ── */}
          <div className="us-modal-header">
            <div
              className={`us-modal-avatar${dpUrl && !dpError ? ' clickable' : ''}`}
              onClick={() => dpUrl && !dpError && openLightbox(dpUrl, false, -1)}
            >
              {dpUrl && !dpError
                ? <img src={dpUrl} alt={profile.user.name} onError={() => setDpError(true)} />
                : profile.user.name.charAt(0).toUpperCase()
              }
            </div>
            <h2>{profile.user.name}</h2>
            <p className="us-modal-email">{profile.user.email}</p>
            <div className="us-modal-stats">
              <span><strong>{profile.connectionCount ?? 0}</strong> Connections</span>
              <span><strong>{profile.followerCount ?? 0}</strong> Followers</span>
            </div>
          </div>

          {/* ── Details ── */}
          <div className="us-modal-details">
            {profile.user.roles && profile.user.roles.length > 0 && (
              <div className="us-modal-detail-row">
                <span className="us-modal-detail-label">Roles</span>
                <div className="us-modal-detail-value us-modal-roles">
                  {profile.user.roles.map((role, i) => (
                    <span key={i} className="us-role-tag">{role}</span>
                  ))}
                </div>
              </div>
            )}
            {profile.user.city && (
              <div className="us-modal-detail-row">
                <span className="us-modal-detail-label">Location</span>
                <span className="us-modal-detail-value">
                  {profile.user.city}, {profile.user.state}
                </span>
              </div>
            )}
          </div>

          {/* ── Gallery ── */}
          {(photos.length > 0 || videos.length > 0) && (
            <div className="us-modal-gallery">
              <div className="us-modal-tabs">
                <button
                  className={`us-modal-tab ${viewMediaType === 'photos' ? 'active' : ''}`}
                  onClick={() => setViewMediaType('photos')}
                >
                  Photos ({photos.length})
                </button>
                <button
                  className={`us-modal-tab ${viewMediaType === 'videos' ? 'active' : ''}`}
                  onClick={() => setViewMediaType('videos')}
                >
                  Videos ({videos.length})
                </button>
              </div>

              {activeMedia.length > 0 ? (
                <div className="us-modal-media-grid">
                  {activeMedia.map((item) => {
                    const url = getMediaUrl(item.file_name);
                    if (!url) return null;
                    const isVideo = item.media_type === 'video';
                    return (
                      <div
                        key={item.id}
                        className={`us-modal-media-item${isVideo ? ' us-modal-media-item--video' : ''}`}
                        onClick={() => openLightbox(url, isVideo, item.id)}
                      >
                        {isVideo ? (
                          <>
                            <video src={url} preload="metadata" />
                            <span className="us-play-icon">▶</span>
                          </>
                        ) : (
                          <img
                            src={url}
                            alt="Photo"
                            onError={(e) => { e.currentTarget.parentElement!.style.display = 'none'; }}
                          />
                        )}
                        {item.is_collab && (
                          <span className="us-collab-media-badge">
                            Tagged as {item.collaborator_role || 'Collaborator'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="us-modal-no-media">
                  <p>No {viewMediaType} yet.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Actions ── */}
          <div className="us-modal-actions">
            {!profile.connectionStatus && (
              <button
                onClick={() => { onConnect(userId); onClose(); }}
                className="us-btn us-btn--filled us-btn--full"
              >
                Connect
              </button>
            )}
            {profile.connectionStatus === 'pending' && (
              <span className="us-status-badge us-status-badge--full">Request Pending</span>
            )}
            {profile.connectionStatus === 'accepted' && (
              <div className="us-modal-action-stack">
                <button
                  onClick={() => { onStartChat(userId); onClose(); }}
                  className="us-btn us-btn--filled us-btn--full"
                >
                  Start Chat
                </button>
                <button
                  onClick={() => void handleDisconnect()}
                  className="us-btn us-btn--ghost us-btn--full"
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            )}
            <button
              onClick={() => void handleFollowToggle()}
              className={`us-btn us-btn--follow us-btn--full${profile.isFollowing ? ' is-following' : ''}`}
              disabled={followUpdating}
            >
              {followUpdating ? 'Updating...' : profile.isFollowing ? 'Unfollow' : 'Follow'}
            </button>
            {false && profile?.connectionStatus === 'accepted' && (
              <button
                onClick={() => { onStartChat(userId); onClose(); }}
                className="us-btn us-btn--filled us-btn--full"
              >
                💬 Start Chat
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Lightbox with collaborators ── */}
      {lightbox && (
        <MediaInteractionModal
          mediaId={lightbox.mediaId}
          src={lightbox.url}
          isVideo={lightbox.isVideo}
          collaborators={(collabMap[lightbox.mediaId] || []).map(c => ({
            ...c,
            user_dp: getMediaUrl(c.user_dp),
          }))}
          collaboratorsLoading={Boolean(collabLoading[lightbox.mediaId])}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default UserSearchPage;
